import React, { useState, lazy, createElement, Component } from 'react';
import { QrCode, Download, Copy, CheckCircle, Ticket } from 'lucide-react';
import type { Event } from '../lib/store';
interface DropQrCodesProps {
  events: Event[];
}
/**
 * ADMIN QR GENERATOR (lives in the Verify tab).
 *
 * Every admin-created drop already has a unique drop code (e.g. DROP-7K2M).
 * This turns each code into a scannable QR that deep-links to the app with
 * "?drop=<CODE>" — scanning it sends the customer straight into registration
 * with that drop pre-matched, skipping the manual code-entry step entirely.
 *
 * QR images are rendered via a public QR image endpoint (no build dependency),
 * so they print/download as PNGs the staff can post at the location.
 */
export function DropQrCodes({
  events
}: DropQrCodesProps) {
  const [copiedCode, setCopiedCode] = useState<string | null>(null);
  // The base URL the QR points at. The admin is opened at the "/admin" path, so
  // we must strip that segment (and any query/hash) and point the QR at the
  // CONSUMER app root — otherwise scanning would reopen the admin platform.
  const consumerPath = window.location.pathname.replace(/\/+$/, '') // drop trailing slashes
  .replace(/\/admin$/, ''); // drop the /admin segment if present
  const appBase = `${window.location.origin}${consumerPath}/`;
  const linkFor = (code: string) => `${appBase}?drop=${encodeURIComponent(code)}`;
  const qrSrc = (code: string, size = 260) => `https://api.qrserver.com/v1/create-qr-code/?size=${size}x${size}&margin=10&data=${encodeURIComponent(linkFor(code))}`;
  const copyLink = async (code: string) => {
    try {
      await navigator.clipboard.writeText(linkFor(code));
      setCopiedCode(code);
      setTimeout(() => setCopiedCode(null), 2000);
    } catch {
      setCopiedCode(null);
    }
  };
  const downloadQr = async (code: string, title: string) => {
    try {
      const res = await fetch(qrSrc(code, 600));
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `qr-${code}-${title.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}.png`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      // If the fetch is blocked, fall back to opening the image in a new tab.
      window.open(qrSrc(code, 600), '_blank');
    }
  };
  return <div className="bg-gray-900 rounded-2xl p-6 border border-gray-800" data-id="element-364" data-mp="jaa3rph9r8qm">
      <h2 className="text-lg font-bold mb-1 flex items-center" data-id="element-365" data-mp="faj4exdofkah">
        <QrCode className="w-5 h-5 mr-2 text-brand-yellow" data-id="element-366" data-mp="aa4z3n3gmog5" />
        Drop QR Codes
      </h2>
      <p className="text-gray-400 text-sm mb-5" data-id="element-367" data-mp="5badyei0snsv">
        Each QR is tied to a drop's code. Scanning it opens the app, matches the
        drop automatically and skips the code-entry step — the customer lands
        straight on registration.
      </p>

      {events.length === 0 ? <div className="bg-gray-950 border border-gray-800 rounded-xl p-8 text-center" data-id="element-368" data-mp="2q3vrwj6b54bl">
          <Ticket className="w-8 h-8 text-gray-600 mx-auto mb-3" data-id="element-369" data-mp="2fvhtp6e2qzoy" />
          <p className="text-gray-400 text-sm" data-id="element-370" data-mp="2kxgxuihn20my">
            No drops yet. Create a drop in the Drops tab — a QR code will appear
            here automatically.
          </p>
        </div> : <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-371" data-mp="2una6bamhhzmz">
          {events.map(e => <div key={e.id} className="bg-gray-950 border border-gray-800 rounded-xl p-4 flex flex-col items-center" data-id="element-372" data-mp="2ynamazp8eiw5">
              <div className="text-center mb-3" data-id="element-373" data-mp="38mjcdhgz7dd8">
                <h3 className="font-bold text-white leading-tight" data-id="element-374" data-mp="38mt1z3y3ziie">
                  {e.title}
                </h3>
                <p className="text-xs text-blue-400" data-id="element-375" data-mp="3dja10t6ujha9">{e.brand}</p>
              </div>

              {/* QR image (white quiet-zone for reliable scanning) */}
              <div className="bg-white rounded-xl p-2 mb-3" data-id="element-376" data-mp="1j8d4t1945hyq">
                <img src={qrSrc(e.code)} alt={`QR code for ${e.title} (${e.code})`} width={150} height={150} className="w-[150px] h-[150px] object-contain" loading="lazy" data-id="element-377" data-mp="39ofcqo8y41uh" />
              </div>

              <span className="font-mono text-sm font-bold text-brand-yellow mb-3" data-id="element-378" data-mp="3dpiujgak07yc">
                {e.code}
              </span>

              <div className="flex w-full gap-2" data-id="element-379" data-mp="2q19d0qkdi6d0">
                <button onClick={() => downloadQr(e.code, e.title)} className="flex-1 flex items-center justify-center gap-1.5 bg-brand-blue hover:bg-brand-blue-dark text-white rounded-lg py-2 text-xs font-bold transition-colors" data-id="element-380" data-mp="2lyuq9vdl2mgg">
                  <Download className="w-3.5 h-3.5" data-id="element-381" data-mp="2uxpy1bwa04lv" />
                  Download
                </button>
                <button onClick={() => copyLink(e.code)} className="flex-1 flex items-center justify-center gap-1.5 bg-gray-800 hover:bg-gray-700 text-gray-200 rounded-lg py-2 text-xs font-bold transition-colors" title="Copy the scan link" data-id="element-382" data-mp="22avhgq2kxpdo">
                  {copiedCode === e.code ? <>
                      <CheckCircle className="w-3.5 h-3.5 text-green-400" data-id="element-383" data-mp="8q204qo8uaw2" />
                      Copied
                    </> : <>
                      <Copy className="w-3.5 h-3.5" data-id="element-384" data-mp="11hbwgvv3dxwl" />
                      Copy link
                    </>}
                </button>
              </div>
            </div>)}
        </div>}
    </div>;
}